/** @jsxRuntime classic */
/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { Link } from '@bottlebooks/gatsby-theme-base/src';
import { GatsbyImage } from 'gatsby-plugin-image';
import shuffle from 'lodash/shuffle';
import React from 'react';
import { jsx } from 'theme-ui';
import getCloudinaryImage from '../getCloudinaryImage';
import { ads, type AdDefinition } from './ads';

const allKeys = Object.keys(ads);

type TAdContext = {
  adKeys: typeof allKeys;
  ref: React.MutableRefObject<number>;
};

const AdContext = React.createContext<TAdContext | undefined>(undefined);

export function AdProvider({ adKeys = allKeys, children }) {
  const ref = React.useRef(0);
  const shuffledKeys = React.useMemo(() => shuffle(adKeys), [adKeys]);
  return (
    <AdContext.Provider value={{ adKeys: shuffledKeys, ref }}>
      {children}
    </AdContext.Provider>
  );
}

function useNextAd() {
  const context = React.useContext(AdContext);
  if (!context)
    throw new Error(
      'BannerAd was used without an AdContext. Add AdContext to the layout or the page wrapper.'
    );
  const { adKeys, ref } = context;
  const next = React.useMemo(() => {
    const key = adKeys[ref.current];
    ref.current = (ref.current + 1) % adKeys.length;
    return key;
  }, [ref, adKeys]);
  return next;
}

export default function BannerAd(props: { className?: string }) {
  const next = useNextAd();

  const currentAd = ads[next];
  if (!currentAd) return null;
  return <Ad {...currentAd} {...props} />;
}

function Ad({
  title,
  href,
  mobileHref = href,
  desktop,
  mobile,
  ...rest
}: AdDefinition) {
  return (
    <>
      <Link
        href={href}
        openInNewTab
        sx={{ display: ['none', null, 'block'] }}
        {...rest}
      >
        <GatsbyImage
          image={getCloudinaryImage({
            src: desktop.publicId,
            width: 728,
            height: 90,
            layout: 'constrained',
            loader: ({ width, height, src, quality }) =>
              `https://res.cloudinary.com/bottlebooks/image/upload/c_fill,g_faces:auto,f_auto,q_${quality},w_${width},h_${height}/${src}`,
            sizes: '(max-width: 728px) 100vw, 728px',
          })}
          alt={title}
        />
      </Link>
      <Link
        href={mobileHref}
        openInNewTab
        sx={{ display: ['block', null, 'none'] }}
        {...rest}
      >
        <GatsbyImage
          image={getCloudinaryImage({
            src: mobile.publicId,
            width: 300,
            height: 200,
            layout: 'constrained',
            loader: ({ width, height, src, quality }) =>
              `https://res.cloudinary.com/bottlebooks/image/upload/c_fill,g_faces:auto,f_auto,q_${quality},w_${width},h_${height}/${src}`,
            sizes: '(max-width: 300px) 100vw, 300px',
          })}
          alt={title}
        />
      </Link>
    </>
  );
}
